import { gql } from "@apollo/client";

export enum NewsState {
    Editing = 10,
    Published = 20
}

const REALESTATE_NEWS_FRAGMENT = gql`
    fragment realestate_news on common_realestates {
        newsitems: newsrealestates(
            where: { news: { news_mls: { language: { _eq: $language } } } }
            order_by: { news: { timestamp: desc_nulls_last } }
        ) {
            newsitem: news {
                id
                timestamp
                news: news_mls(limit: 1) {
                    id
                    lead
                    text
                    title
                }
                newsfiles: v_newsfiles(where: { type: { _eq: 601 } }, limit: 1) {
                    fileid
                }
                attachments: v_newsfiles(where: { type: { _in: [602, 605] }, role: { _eq: 32 } }) {
                    fileid
                    name
                }
            }
        }
    }
`;

export const GET_COOWNER_NEWS = gql`
    ${REALESTATE_NEWS_FRAGMENT}
    query GetCoownerNews($language: String = "de", $realestateid: uuid!) {
        realestates: common_realestates(where: { id: { _eq: $realestateid } }) {
            ...realestate_news
        }
    }
`;

export const GET_COOWNER_NEWS_IT2 = gql`
    ${REALESTATE_NEWS_FRAGMENT}
    query GetCoownerNewsIT2($language: String = "de", $realestateid: uuid!) {
        realestates: common_realestates(
            where: { _or: [{ id: { _eq: $realestateid } }, { consolidationid: { _eq: $realestateid } }] }
        ) {
            ...realestate_news
        }
    }
`;

export const GET_MANAGER_NEWS = gql`
    query GetManagerNews($language: String = "de", $realestateid: uuid!) {
        newsitems: news_news(
            order_by: { updated: desc_nulls_last }
            where: {
                news_mls: { language: { _eq: $language } }
                newsrealestates: { realestateid: { _eq: $realestateid } }
            }
        ) {
            id
            timestamp
            state
            news: news_mls {
                id
                title
            }
        }

    }
`;

// All news of the customer for the page "News", and the realestates of the user for the filter
// "Meine Liegenschaften". These are the same as for the assemblies and tickets: the realestates the user manages
// or has full access to.
export const GET_NEWS_OVERVIEW = gql`
    query GetNewsOverview($language: String = "de") {
        newsitems: news_news(
            order_by: { updated: desc_nulls_last }
            where: { news_mls: { language: { _eq: $language } } }
        ) {
            id
            timestamp
            updated
            state
            author
            news: news_mls(where: { language: { _eq: $language } }, limit: 1) {
                id
                title
            }
            newsrealestates(order_by: { realestate: { number: asc } }) {
                realestateid
                realestate {
                    id
                    name
                    number
                }
            }
        }
        myRealestates: cond_assemblys_by_realestate_user {
            id
            realestateid
        }
    }
`;

// news.news only holds the id of the author.
export const GET_NEWS_AUTHORS = gql`
    query GetNewsAuthors($userids: [uuid!]!) {
        authors: ums_users(where: { id: { _in: $userids } }) {
            id
            name1
            name2
            email
        }
    }
`;

export const GET_NEWS_PREVIEW_INFORMATION_BY_ID = gql`
    query GetNewsPreviewInformationById($newsid: uuid!, $language: String = "de") {
        news: news_v_news_preview(where: { id: { _eq: $newsid }, language: { _eq: $language } }, limit: 1) {
            id
            timestamp
            state
            statelabel
            title
            lead
            text
            fileid
            author
            newsfiles(where: { type: { _neq: 601 } }) {
                id
                fileid
                name
            }
            newsevents: newsevents_aggregate {
                events: aggregate {
                    count
                }
            }
        }
        newsrealestates: news_newsrealestates(where: { newsid: { _eq: $newsid } }) {
            realestateid
            realestate {
                condominium
            }
        }
    }
`;

// Counts persons, not access rows: someone living in two realestates of the news is counted once.
export const GET_NEWS_RECIPIENTS_COUNT = gql`
    query GetNewsRecipientsCount($realestateids: [uuid!]!) {
        active_coowners_count: common_v_coowners_by_realestateaccess_aggregate(
            where: { realestateid: { _in: $realestateids } }
        ) {
            aggregate {
                count(columns: userid, distinct: true)
            }
        }
        active_tenants_count: common_v_tenants_by_realestateaccess_aggregate(
            where: { realestateid: { _in: $realestateids } }
        ) {
            aggregate {
                count(columns: userid, distinct: true)
            }
        }
    }
`;

export const SUBCRIPTION_GET_LAST_INSERTED_NEWS_EVENT = gql`
    subscription GetLastNewsEventById($newsid: uuid!) {
        noti_newsevents(order_by: { inserted: desc }, limit: 1, where: { newsid: { _eq: $newsid } }) {
            inserted
        }
    }
`;

export const GET_NEWS_DETAILS_BY_ID = gql`
    query GetNewsDetailsById($newsid: uuid!, $language: String = "de") {
        newsitems: news_news(
            order_by: { timestamp: desc_nulls_last }
            where: { news_mls: { language: { _eq: $language } }, id: { _eq: $newsid } }
        ) {
            id
            timestamp
            state
            publishfrom
            startnotification
            news: news_mls {
                id
                title
                lead
                text
            }
            newsfileimage: v_newsfiles(where: { type: { _eq: 601 } }) {
                id
                fileid
                name
            }
            newsfiles: v_newsfiles(where: { type: { _in: [602, 605] }, _and: { role: { _eq: 30 } } }) {
                id
                fileid
                name
            }
        }
    }
`;

// realestateids holds the realestate the mask was opened from, or nothing when opened from the page "News".
export const GET_NEWS_EDIT_REALESTATES = gql`
    query GetNewsEditRealestates($newsid: uuid!, $realestateids: [uuid!]!) {
        newsrealestates: news_newsrealestates(
            where: { newsid: { _eq: $newsid } }
            order_by: { realestate: { number: asc } }
        ) {
            realestateid
            realestate {
                id
                name
                number
                consolidationid
                condominium
            }
        }
        currentRealestate: common_realestates(where: { id: { _in: $realestateids } }) {
            id
            name
            number
            consolidationid
            condominium
        }
    }
`;

export const DELETE_NEWS_BY_ID = gql`
    mutation DeleteNewsById($newsid: uuid!) {
        delete_news_news(where: { id: { _eq: $newsid } }) {
            affected_rows
        }
    }
`;

export const DELETE_NEWSFILE_BY_FILEID = gql`
    mutation DeleteNewsFileByFileId($fileid: uuid!) {
        delete_file_newsfiles(where: { fileid: { _eq: $fileid } }) {
            affected_rows
        }
    }
`;

export const DELETE_NEWSFILES_OF_NEWS = gql`
    mutation DeleteNewsFilesOfNews($newsid: uuid!, $fileids: [uuid!]!) {
        delete_file_newsfiles(where: { newsid: { _eq: $newsid }, fileid: { _in: $fileids } }) {
            affected_rows
        }
    }
`;

export const GET_NEWS_NOTIFICATION_STATE_BY_ASSEMBLYID = gql`
    query NewsEventState($newsid: uuid!) {
        NewsNotificationState: noti_v_notification_news_state(where: { newsid: { _eq: $newsid } }) {
            name
            email
            datetime
            state
        }
    }
`;

// Files of the selected realestates. realestateids also holds the IT2 consolidations (Mandanten) of the
// selected realestates, consolidationids the selected realestates themselves (files of their realestates).
export const GET_NEWS_SELECTION_FILES_MANAGER = gql`
    query GetNewsSelectionFilesManager($realestateids: [uuid!]!, $consolidationids: [uuid!]!) {
        accountlistfiles: file_v_accountlistfiles(
            where: { _or: [{ realestateid: { _in: $realestateids } }, { consolidationid: { _in: $consolidationids } }] }
            order_by: [{ filedate: desc }, { fileid: desc }]
            distinct_on: [filedate, fileid]
        ) {
            id
            type
            fileid
            name
            mimetype
            filedate
        }
        realestatefiles: file_v_realestatefiles(
            where: { _or: [{ realestateid: { _in: $realestateids } }, { consolidationid: { _in: $consolidationids } }] }
            order_by: [{ filedate: desc }, { fileid: desc }]
            distinct_on: [filedate, fileid]
        ) {
            id
            type
            fileid
            name
            mimetype
            filedate
        }
    }
`;

export const INSERT_NEWS_FILE_MANAGER = gql`
    mutation InsertNewsFileManager($objects: [file_newsfiles_insert_input!]!) {
        insertedfiles: insert_file_newsfiles(objects: $objects) {
            returning {
                id
                fileid
            }
        }
    }
`;

const REALESTATE_TENANT_NEWS_FRAGMENT = gql`
    fragment realestate_tenant_news on common_realestates {
        newsitems: newsrealestates(
            where: { news: { news_mls: { language: { _eq: $language } } } }
            order_by: { news: { timestamp: desc_nulls_last } }
        ) {
            newsitem: news {
                id
                timestamp
                news: news_mls(limit: 1) {
                    id
                    lead
                    text
                    title
                }
                newsfiles: v_newsfiles(where: { type: { _eq: 601 } }, limit: 1) {
                    fileid
                }
                attachments: v_newsfiles(where: { type: { _in: [602, 605] }, role: { _eq: 33 } }) {
                    fileid
                    name
                }
            }
        }
    }
`;

export const GET_TENANT_NEWS = gql`
    ${REALESTATE_TENANT_NEWS_FRAGMENT}
    query GetTenantNews($language: String = "de", $realestateid: uuid!) {
        realestates: common_realestates(where: { id: { _eq: $realestateid } }) {
            ...realestate_tenant_news
        }
    }
`;

export const GET_TENANT_NEWS_IT2 = gql`
    ${REALESTATE_TENANT_NEWS_FRAGMENT}
    query GetTenantNewsIT2($language: String = "de", $realestateid: uuid!) {
        realestates: common_realestates(
            where: { _or: [{ id: { _eq: $realestateid } }, { consolidationid: { _eq: $realestateid } }] }
        ) {
            ...realestate_tenant_news
        }
    }
`;
